import {
  useState,
  type ButtonHTMLAttributes,
  type ChangeEvent,
  type FormEvent,
  type InputHTMLAttributes,
  type ReactNode,
} from "react";
import { useAuth } from "@/contexts/AuthContext";
import { readEnvelope, request, type ApiEnvelope } from "@/lib/api";
import "./LoginForm.css";

export interface LoginFormProps {
  onSuccess?: () => void;
}

const VIEWS = {
  LOGIN: "login",
  FORGOT_REQUEST: "forgot-request",
  FORGOT_VERIFY: "forgot-verify",
  FORGOT_RESET: "forgot-reset",
} as const;

type View = (typeof VIEWS)[keyof typeof VIEWS];

type SlideDirection = "enter" | "next" | "back";

function Field({
  id,
  label,
  aside,
  className,
  ...props
}: {
  id: string;
  label: string;
  aside?: ReactNode;
} & InputHTMLAttributes<HTMLInputElement>) {
  return (
    <div className="al-auth-field">
      <div className="al-auth-label-row">
        <label className="al-auth-label" htmlFor={id}>
          {label}
        </label>
        {aside}
      </div>
      <input
        id={id}
        className={className ? `al-auth-input ${className}` : "al-auth-input"}
        {...props}
      />
    </div>
  );
}

function Notices({ error, info }: { error: string; info: string }) {
  if (!error && !info) return null;
  return (
    <div className="al-auth-notices">
      {error ? (
        <p className="al-auth-banner al-auth-banner--error" role="alert">
          {error}
        </p>
      ) : null}
      {info ? (
        <p className="al-auth-banner al-auth-banner--info" role="status">
          {info}
        </p>
      ) : null}
    </div>
  );
}

function TextButton({ children, ...props }: ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button type="button" className="al-auth-text" {...props}>
      {children}
    </button>
  );
}

const LoginForm = ({ onSuccess }: LoginFormProps) => {
  const [view, setView] = useState<View>(VIEWS.LOGIN);
  const [direction, setDirection] = useState<SlideDirection>("enter");
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [verificationCode, setVerificationCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const { login } = useAuth();

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData((previous) => ({ ...previous, [name]: value }));
    setError("");
  };

  const resetMessages = () => {
    setError("");
    setInfo("");
  };

  const openView = (next: View, slide: Exclude<SlideDirection, "enter">) => {
    resetMessages();
    setDirection(slide);
    setView(next);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);
    resetMessages();

    const result = await login(formData.email, formData.password);

    if (result.success) {
      onSuccess?.();
    } else if (!result.accessDenied) {
      setError(result.message ?? "Login failed.");
    }

    setLoading(false);
  };

  const handleForgotRequest = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);
    resetMessages();
    try {
      const payload = await readEnvelope(
        await request(
          "/api/auth/forgot-password/request",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email: formData.email }),
          },
          { promptOnUnauthorized: false },
        ),
      );
      setInfo(
        payload.message ||
          "If an account exists for that email, a verification code has been sent.",
      );
      setVerificationCode("");
      setResetToken("");
      setNewPassword("");
      setConfirmPassword("");
      setView(VIEWS.FORGOT_VERIFY);
    } catch {
      setError("Unable to send verification code. Please try again.");
    }
    setLoading(false);
  };

  const handleForgotVerify = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);
    resetMessages();
    try {
      const payload = await readEnvelope<ApiEnvelope<{ token?: string }> & { token?: string }>(
        await request(
          "/api/auth/forgot-password/verify",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email: formData.email, code: verificationCode.trim() }),
          },
          { promptOnUnauthorized: false },
        ),
      );
      const token = payload.data?.token || (payload as { token?: string }).token;
      if (!payload.success || !token) {
        setError(payload.message || "Unable to verify code. Please try again.");
        setLoading(false);
        return;
      }
      setResetToken(token);
      setInfo("Code verified. Choose a new password below.");
      setView(VIEWS.FORGOT_RESET);
    } catch {
      setError("Unable to verify code. Please try again.");
    }
    setLoading(false);
  };

  const handleForgotReset = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    resetMessages();
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      const payload = await readEnvelope(
        await request(
          "/api/auth/reset-password",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ token: resetToken, newPassword }),
          },
          { promptOnUnauthorized: false },
        ),
      );
      if (payload.success === false) {
        setError(payload.message || "Unable to reset password. Please request a new code.");
        setLoading(false);
        return;
      }
      setInfo("Password updated successfully. You can sign in with your new password.");
      setFormData((previous) => ({ ...previous, password: "" }));
      setVerificationCode("");
      setNewPassword("");
      setConfirmPassword("");
      setResetToken("");
      setDirection("back");
      setView(VIEWS.LOGIN);
    } catch {
      setError("Unable to reset password. Please request a new code.");
    }
    setLoading(false);
  };

  const panelClass =
    direction === "enter" ? "al-auth-panel" : `al-auth-panel al-auth-panel--${direction}`;

  return (
    <div className="al-auth">
      <section className="al-auth-card" aria-labelledby="al-auth-title">
        <div className="al-auth-brand">
          <span className="al-auth-mark" aria-hidden="true">
            <img src="/brand/acuity-mark.png" alt="" />
          </span>
          <span className="al-auth-eyebrow">AcuityLearn</span>
        </div>
        <div className="al-auth-stage">
          <div key={view} className={panelClass}>
            {view === VIEWS.LOGIN && (
              <>
                <header className="al-auth-head">
                  <h1 id="al-auth-title" className="al-auth-title">
                    Sign in
                  </h1>
                  <p className="al-auth-subtitle">Use your UIS account to access AcuityLearn.</p>
                </header>
                <form onSubmit={handleSubmit} className="al-auth-form">
                  <Field
                    id="auth-email"
                    label="Email"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    autoComplete="email"
                    spellCheck={false}
                  />
                  <Field
                    id="auth-password"
                    label="Password"
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    autoComplete="current-password"
                    aside={
                      <TextButton
                        onClick={() => openView(VIEWS.FORGOT_REQUEST, "next")}
                        disabled={loading}
                      >
                        Forgot password?
                      </TextButton>
                    }
                  />
                  <Notices error={error} info={info} />
                  <button className="al-auth-submit" type="submit" disabled={loading}>
                    {loading ? "Signing in…" : "Sign in"}
                  </button>
                </form>
              </>
            )}

            {view === VIEWS.FORGOT_REQUEST && (
              <>
                <header className="al-auth-head">
                  <h1 id="al-auth-title" className="al-auth-title">
                    Reset password
                  </h1>
                  <p className="al-auth-subtitle">
                    Enter your UIS email and we will send a verification code.
                  </p>
                </header>
                <form onSubmit={handleForgotRequest} className="al-auth-form">
                  <Field
                    id="auth-reset-email"
                    label="Email"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    autoComplete="email"
                    spellCheck={false}
                  />
                  <Notices error={error} info={info} />
                  <button className="al-auth-submit" type="submit" disabled={loading}>
                    {loading ? "Sending code…" : "Send verification code"}
                  </button>
                </form>
                <div className="al-auth-footer">
                  <TextButton onClick={() => openView(VIEWS.LOGIN, "back")} disabled={loading}>
                    Back to sign in
                  </TextButton>
                </div>
              </>
            )}

            {view === VIEWS.FORGOT_VERIFY && (
              <>
                <header className="al-auth-head">
                  <h1 id="al-auth-title" className="al-auth-title">
                    Enter verification code
                  </h1>
                  <p className="al-auth-subtitle">
                    We sent a code to {formData.email || "your email"}.
                  </p>
                </header>
                <form onSubmit={handleForgotVerify} className="al-auth-form">
                  <Field
                    id="auth-code"
                    label="Verification code"
                    className="al-auth-input--code"
                    type="text"
                    name="code"
                    value={verificationCode}
                    onChange={(event) => setVerificationCode(event.target.value)}
                    required
                    autoComplete="one-time-code"
                    spellCheck={false}
                  />
                  <Notices error={error} info={info} />
                  <button className="al-auth-submit" type="submit" disabled={loading}>
                    {loading ? "Verifying…" : "Verify code"}
                  </button>
                </form>
                <div className="al-auth-footer">
                  <TextButton
                    onClick={() => openView(VIEWS.FORGOT_REQUEST, "back")}
                    disabled={loading}
                  >
                    Resend code
                  </TextButton>
                </div>
              </>
            )}

            {view === VIEWS.FORGOT_RESET && (
              <>
                <header className="al-auth-head">
                  <h1 id="al-auth-title" className="al-auth-title">
                    Choose a new password
                  </h1>
                  <p className="al-auth-subtitle">Create a new password for your UIS account.</p>
                </header>
                <form onSubmit={handleForgotReset} className="al-auth-form">
                  <Field
                    id="auth-new-password"
                    label="New password"
                    type="password"
                    name="newPassword"
                    value={newPassword}
                    onChange={(event) => setNewPassword(event.target.value)}
                    required
                    autoComplete="new-password"
                  />
                  <Field
                    id="auth-confirm-password"
                    label="Confirm password"
                    type="password"
                    name="confirmPassword"
                    value={confirmPassword}
                    onChange={(event) => setConfirmPassword(event.target.value)}
                    required
                    autoComplete="new-password"
                  />
                  <Notices error={error} info={info} />
                  <button className="al-auth-submit" type="submit" disabled={loading}>
                    {loading ? "Updating…" : "Update password"}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default LoginForm;
